import {logUtishaDebug, logUtishaError, logUtishaTrace} from 'src/app/util/log';
import {CanvasRenderer} from './CanvasRenderer';
import {Subscription} from 'rxjs';

export type RendererSearchOptions = {
  name?: string,
  priority?: number,
  excludingName?: string
}

export class CanvasRenderingPipeline {
  private videoElement: HTMLVideoElement | null = null;
  private _canvas: HTMLCanvasElement | null = null;
  private animationFrameId: number | null = null;
  private lastFrameTime = 0;
  private frameCount = 0;
  private frameTimeSum = 0;
  private targetFps = 25; // Start with 25 FPS
  private minFps = 10;    // Minimum acceptable FPS
  private maxFps = 30;    // Maximum FPS
  private adaptationInterval = 2000; // Check every 2 seconds
  private lastAdaptationTime = 0;
  private networkQualityScore = 1.0; // 0.0-1.0 scale
  private cpuUsageScore = 1.0;       // 0.0-1.0 scale (independent of network)
  private connectionMonitorInterval: number | null = null;
  private _stream: MediaStream;
  // Silent-black-frame heartbeat: timestamp of the last successful draw.
  // External watchdogs compare this against wall-clock to detect pipeline stalls
  // where the animation loop is alive in name but no pixels are reaching the
  // captured stream (renderer throw, stalled source video, backgrounded tab).
  private lastDrawAt = 0;
  private totalFramesDrawn = 0;
  // Baseline FPS detection — the max FPS this device can actually sustain.
  // Mobile devices often cap at 30 FPS (iOS battery saver, Android low-end).
  // We measure the device's actual ceiling rather than assuming 30.
  private deviceBaselineFps = 30; // Updated after first adaptation window
  private baselineCalibrated = false;
  // Renderer pipeline
  private renderers: CanvasRenderer[] = [];
  private rendererSubscriptions: Map<number, Subscription> = new Map();
  private renderingInProgress = false;
  // rVFC handle — when a renderer provides a source video element, we prefer
  // requestVideoFrameCallback over rAF because it fires exactly when a new
  // frame is ready on the source, giving the lowest-latency/highest-fidelity
  // path and the truest heartbeat for stall detection.
  private rvfcHandle: number | null = null;
  private rvfcSourceVideo: HTMLVideoElement | null = null;


  constructor() {
    this._canvas = document.createElement('canvas');
    this._canvas.width = 1280; // Default width
    this._canvas.height = 720; // Default height
  }

  /**
   * Timestamp (performance.now) of the last successful draw. Used by
   * MediaWatchdog to detect silent pipeline stalls — cases where the loop is
   * alive on paper but no pixels are reaching the captured stream.
   */
  get lastDrawTimestamp(): number {
    return this.lastDrawAt;
  }

  /** Monotonic count of frames actually drawn since start(). */
  get framesDrawn(): number {
    return this.totalFramesDrawn;
  }

  /**
   * Returns true if the pipeline claims to be started but no frame has been
   * drawn in the last `thresholdMs`. This is the silent-black-frame check.
   * Returns false if the pipeline is not started — a stopped pipeline is not
   * "stalled", it's just not running.
   */
  isStalled(thresholdMs: number): boolean {
    if (!this.started) return false;
    if (this.lastDrawAt === 0) {
      // Never drew a frame yet — compare against start-equivalent.
      return false;
    }
    return (performance.now() - this.lastDrawAt) > thresholdMs;
  }

  get stream(): MediaStream {
    return this._stream;
  }

  get canvas(): HTMLCanvasElement | null {
    return this._canvas;
  }

  getCpuUsageScore(): number {
    return this.cpuUsageScore;
  }

  /**
   * Adds a renderer to the rendering pipeline
   * Renderers are sorted by priority (lower priority = lower index)
   * @param renderer The renderer to add
   */
  public addRenderer(renderer: CanvasRenderer): void {
    if (!this._canvas) {
      logUtishaError('Cannot add renderer - no _canvas available');
      return;
    }

    // Initialize the renderer with our _canvas
    renderer.initialize(this._canvas);

    // Subscribe to the destroyed event
    const subscription = renderer.destroyed$.subscribe(() => {
      this.removeRenderer(renderer.id);
    });
    this.rendererSubscriptions.set(renderer.id, subscription);

    // Add to the renderers array in the correct position based on priority
    // Use a temporary array to avoid modification during rendering
    const updatedRenderers = [...this.renderers];

    // Find the correct index based on priority
    let insertIndex = updatedRenderers.length;
    for (let i = 0; i < updatedRenderers.length; i++) {
      if (renderer.priority < updatedRenderers[i].priority) {
        insertIndex = i;
        break;
      }
    }

    // Insert the renderer at the correct position
    updatedRenderers.splice(insertIndex, 0, renderer);

    // Update the renderers array with the new version
    this.renderers = updatedRenderers;

    const rendererNames = this.renderers.map(renderer => `${renderer.name} [id=${renderer.id}, priority=${renderer.priority}]`).join(', ') || 'none';
    logUtishaDebug(`Added renderer ${renderer.name}[${renderer.id}] with priority ${renderer.priority}. Current renderers: ${rendererNames}`);
  }

  /**
   * Removes a renderer from the rendering pipeline by id
   * @param id The id of the renderer to remove
   */
  public removeRenderer(id: number): void {
    // Clean up subscription
    const subscription = this.rendererSubscriptions.get(id);
    if (subscription) {
      subscription.unsubscribe();
      this.rendererSubscriptions.delete(id);
    }

    // Use a temporary array to avoid modification during rendering
    const updatedRenderers = this.renderers.filter(renderer => renderer.id !== id);

    // If a renderer was removed, log it
    if (updatedRenderers.length !== this.renderers.length) {
      const removedRenderer = this.renderers.find(renderer => renderer.id === id);
      logUtishaDebug(`Removed renderer ${removedRenderer?.name || 'unknown'}[${id}]`);
    }

    // Update the renderers array with the new version
    this.renderers = updatedRenderers;
  }

  async start() {
    logUtishaDebug('CanvasRenderingPipeline.start');
    if (!this._canvas || this.renderingInProgress || this.started || this.renderers.length === 0) {
      logUtishaDebug(`Cannot start - canvas not available, rendering in progress, or already started`);
      return;
    }
    // Start monitoring network conditions
    this.startNetworkMonitoring();

    // Start the rendering loop
    this.startRenderingLoop();

    // Create stream with dynamic FPS
    this._stream = this._canvas.captureStream(); // No fixed FPS parameter
    if (!this._stream) {
      throw new Error('Canvas captureStream not supported');
    }
    return this._stream;
  }

  /**
   * Starts the rendering loop to draw all renderers to the _canvas.
   *
   * Prefers HTMLVideoElement.requestVideoFrameCallback when a renderer exposes
   * a source video element (via `getSourceVideoElement()`) — this fires exactly
   * when a new frame is decoded on the source, giving the lowest-latency path
   * and the truest stall signal. Falls back to requestAnimationFrame for other
   * sources or browsers without rVFC support.
   */
  private startRenderingLoop(): void {
    if (this.animationFrameId !== null || this.rvfcHandle !== null) {
      // Already running
      return;
    }

    // Initialize timing variables
    this.lastFrameTime = performance.now();
    this.lastAdaptationTime = performance.now();
    this.frameCount = 0;
    this.frameTimeSum = 0;
    this.lastDrawAt = 0;
    this.totalFramesDrawn = 0;

    // Try to find a source video element from the renderers. If one is
    // available and rVFC is supported, drive the loop from video frame
    // callbacks — one draw per real decoded frame on the source.
    const sourceVideo = this.findSourceVideoElement();
    const rvfcSupported = sourceVideo
      && typeof (sourceVideo as any).requestVideoFrameCallback === 'function';

    if (rvfcSupported) {
      this.rvfcSourceVideo = sourceVideo;
      logUtishaDebug('CanvasRenderingPipeline: using requestVideoFrameCallback loop');
      this.scheduleRvfcDraw();
    } else {
      logUtishaDebug('CanvasRenderingPipeline: using requestAnimationFrame loop');
      this.scheduleRafDraw();
    }
  }

  /**
   * Finds the first renderer that exposes a source HTMLVideoElement. The
   * rVFC path only works when there's a driving video; canvas-only composes
   * (e.g. static overlays) still need rAF.
   */
  private findSourceVideoElement(): HTMLVideoElement | null {
    for (const renderer of this.renderers) {
      const getter = (renderer as any).getSourceVideoElement;
      if (typeof getter === 'function') {
        const el = getter.call(renderer);
        if (el instanceof HTMLVideoElement) return el;
      }
    }
    return null;
  }

  /** Runs one draw pass and records the heartbeat. Shared by rAF and rVFC. */
  private drawOnce(now: number): void {
    if (!this._canvas) return;

    // Mark rendering as in progress to prevent array modifications
    this.renderingInProgress = true;

    // Clear the _canvas before drawing
    const ctx = this._canvas.getContext('2d', { willReadFrequently: true });
    if (ctx) {
      ctx.clearRect(0, 0, this._canvas.width, this._canvas.height);
    }

    // Draw all renderers in order of priority
    for (const renderer of this.renderers) {
      try {
        renderer.render();
      } catch (e: any) {
        // A renderer throwing must never kill the loop — if it does, the
        // captured stream silently goes black. Log and keep going.
        logUtishaError(`CanvasRenderingPipeline: renderer ${renderer.name} threw during render`, e?.message || e);
      }
    }

    // Rendering complete
    this.renderingInProgress = false;

    // Heartbeat — the critical bit. Any external watchdog reads lastDrawAt
    // to distinguish "loop alive and drawing" from "loop alive but stalled".
    this.lastDrawAt = now;
    this.totalFramesDrawn++;

    // FPS adaptation bookkeeping
    const elapsed = now - this.lastFrameTime;
    this.frameTimeSum += elapsed;
    this.frameCount++;
    this.lastFrameTime = now;

    if (now - this.lastAdaptationTime > this.adaptationInterval) {
      this.adaptFrameRate();
      this.lastAdaptationTime = now;
      this.frameCount = 0;
      this.frameTimeSum = 0;
    }
  }

  private scheduleRafDraw(): void {
    const drawFrame = () => {
      if (!this._canvas) return;
      const now = performance.now();
      const elapsed = now - this.lastFrameTime;
      const targetFrameInterval = 1000 / this.targetFps;
      if (elapsed >= targetFrameInterval) {
        this.drawOnce(now);
      }
      this.animationFrameId = requestAnimationFrame(drawFrame);
    };
    drawFrame();
  }

  private scheduleRvfcDraw(): void {
    const video = this.rvfcSourceVideo;
    if (!video) return;
    const draw = (_now: number, _metadata: any) => {
      if (!this._canvas || this.rvfcSourceVideo !== video) return;
      // Respect target FPS so network adaptation still applies. If the source
      // runs at 60fps but target is 25, skip frames deterministically.
      const perfNow = performance.now();
      const elapsed = perfNow - this.lastFrameTime;
      const targetFrameInterval = 1000 / this.targetFps;
      if (elapsed >= targetFrameInterval) {
        this.drawOnce(perfNow);
      }
      this.rvfcHandle = (video as any).requestVideoFrameCallback(draw);
    };
    this.rvfcHandle = (video as any).requestVideoFrameCallback(draw);
  }

  private adaptFrameRate() {
    // Calculate current actual FPS
    if (this.frameCount === 0) {
      return;
    }

    const actualFps = this.frameCount / (this.frameTimeSum / 1000);

    // --- Baseline calibration ---
    // On the first window where we're hitting close to maxFps, record the device's
    // actual ceiling. Mobile devices (iOS battery saver, Android low-end) often cap
    // at 30 FPS or lower. We use the actual ceiling for relative deviation, not a
    // hardcoded 30.
    if (!this.baselineCalibrated && actualFps >= this.maxFps * 0.9) {
      this.deviceBaselineFps = actualFps;
      this.baselineCalibrated = true;
      logUtishaTrace(() => `Adaptive FPS: device baseline calibrated at ${this.deviceBaselineFps.toFixed(1)}`);
    }

    // --- CPU score (independent of network) ---
    // Compare actual FPS against the device's own baseline, not against targetFps.
    // This prevents network-driven target changes from polluting the CPU signal.
    const fpsRatio = actualFps / this.deviceBaselineFps;

    if (fpsRatio < 0.7) {
      // Significant frame drops relative to what this device can do
      this.cpuUsageScore = Math.max(0.1, this.cpuUsageScore * 0.85);
    } else if (fpsRatio < 0.85) {
      // Moderate frame drops
      this.cpuUsageScore = Math.max(0.3, this.cpuUsageScore * 0.93);
    } else if (fpsRatio >= 0.93) {
      // Device is keeping up — recover faster than the old 5% rate.
      // 15% recovery per 2s window → ~8s from 0.3→1.0 (was ~28s).
      this.cpuUsageScore = Math.min(1.0, this.cpuUsageScore * 1.15);
    }

    // --- Target FPS (driven by network only) ---
    // CPU pressure is handled separately by PerformanceMonitor → canvas resolution / audio throttle.
    // Target FPS adapts only to network conditions to avoid the feedback loop where
    // low network → low target → CPU appears fine → CPU score recovers → repeat.
    const newTargetFps = this.minFps + this.networkQualityScore * (this.maxFps - this.minFps);
    // Smooth transition to new target (don't change too dramatically)
    this.targetFps = 0.7 * this.targetFps + 0.3 * newTargetFps;
    this.targetFps = Math.max(this.minFps, Math.min(this.maxFps, this.targetFps));
    logUtishaTrace(() => `Adaptive FPS: network=${this.networkQualityScore.toFixed(2)}, ` +
      `CPU=${this.cpuUsageScore.toFixed(2)}, fpsRatio=${fpsRatio.toFixed(2)}, ` +
      `actual=${actualFps.toFixed(1)}, baseline=${this.deviceBaselineFps.toFixed(1)}, target=${this.targetFps.toFixed(1)}`);
  }

  private startNetworkMonitoring() {
    // Clean up any existing interval
    if (this.connectionMonitorInterval) {
      clearInterval(this.connectionMonitorInterval);
    }

    const updateNetworkQuality = () => {
      // Use Network Information API if available
      const connection = (navigator as any).connection;

      if (connection) {
        // Get download speed (in Mbps)
        if (connection.downlink) {
          // Score based on bandwidth (assuming 2Mbps is good quality)
          this.networkQualityScore = Math.min(1.0, connection.downlink / 2);
        }

        // Consider connection type
        if (connection.effectiveType) {
          switch (connection.effectiveType) {
            case 'slow-2g':
              this.networkQualityScore = Math.min(this.networkQualityScore, 0.2);
              break;
            case '2g':
              this.networkQualityScore = Math.min(this.networkQualityScore, 0.4);
              break;
            case '3g':
              this.networkQualityScore = Math.min(this.networkQualityScore, 0.7);
              break;
          }
        }

        // Consider round-trip time if available
        if (connection.rtt) {
          // RTT over 300ms is problematic
          const rttScore = Math.min(1.0, Math.max(0.3, 1 - (connection.rtt / 300)));
          this.networkQualityScore = Math.min(this.networkQualityScore, rttScore);
        }
      } else {
        // If Network Information API is unavailable, use a conservative default
        // or implement a fallback measurement method
        this.networkQualityScore = 0.8; // Default to slightly conservative
      }
    };

    // Update immediately and then periodically
    updateNetworkQuality();
    this.connectionMonitorInterval = window.setInterval(updateNetworkQuality, 2000);
  }

  get started() {
    return this.animationFrameId != null || this.rvfcHandle != null;
  }

  stop() {
    logUtishaDebug('CanvasRenderingPipeline.stop');
    // Cancel animation frame
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    // Cancel rVFC. Not all browsers expose cancelVideoFrameCallback; losing
    // a tail callback is harmless because drawOnce is guarded by
    // `this.rvfcSourceVideo !== video` before it runs.
    if (this.rvfcHandle !== null && this.rvfcSourceVideo) {
      const cancel = (this.rvfcSourceVideo as any).cancelVideoFrameCallback;
      if (typeof cancel === 'function') {
        try { cancel.call(this.rvfcSourceVideo, this.rvfcHandle); } catch (_) { /* best-effort */ }
      }
      this.rvfcHandle = null;
    }
    this.rvfcSourceVideo = null;

    // Defensive reset — if stop() is called while a frame was in progress
    // (shouldn't happen in single-threaded JS, but guards against edge cases)
    this.renderingInProgress = false;
    // Clean up network monitoring
    if (this.connectionMonitorInterval) {
      clearInterval(this.connectionMonitorInterval);
      this.connectionMonitorInterval = null;
    }
    // Clean up all renderers
    // Make a copy of the ids to avoid issues with modification during iteration
    const rendererIds = this.renderers.map(renderer => renderer.id);
    rendererIds.forEach(id => this.removeRenderer(id));
    if (this._stream) {
      this._stream.getTracks().forEach(t => t.stop());
      this._stream = undefined as any;  // Clear stale reference so start() creates a fresh stream
    }
    // Legacy cleanup for backward compatibility
    if (this.videoElement) {
      this.videoElement.pause();
      this.videoElement.src = '';
    }
  }

  getRenderersWithName(name: string): CanvasRenderer[] {
    return this.renderers.filter(renderer => renderer.name === name);
  }

  hasRendererWithName(name: string): boolean {
    return this.getRenderersWithName(name).length > 0;
  }

  searchRenderers(options: RendererSearchOptions) {
    if (options.name) {
      return this.renderers.filter(renderer => renderer.name === options.name);
    }
    if (options.priority) {
      return this.renderers.filter(renderer => renderer.priority === options.priority);
    }
    if (options.excludingName) {
      return this.renderers.filter(renderer => renderer.name !== options.excludingName);
    }
    return this.renderers;
  }
}
